import type { SignInFailure, SignInOutcome } from "./sign-in";

export type ListingRequestOutcome =
  | { leadId: string; leadNumber: string; alreadyRequested: boolean }
  | { error: string };

export interface SignedIn {
  status: "signed_in";
  isNew: boolean;
  quoteClaim?: "redeemed" | "failed";
  listingRequest?: ListingRequestOutcome;
}

export type SignInErrorReason =
  | SignInFailure
  | "invalid_input"
  | "account_exists"
  | "weak_password"
  | "account_refused";

export type SignInResult =
  | SignedIn
  | { status: "needs_code" }
  | { status: "needs_hosted" }
  | { status: "error"; message: string; reason: SignInErrorReason };

export const INVALID_CODE_MESSAGE = "That code didn't work. Check the digits and try again.";

const FAILURE_MESSAGES: Record<SignInFailure, string> = {
  invalid_credentials:
    "That email and password don't match. Try again, reset your password, or email yourself a code.",
  invalid_code: INVALID_CODE_MESSAGE,
  expired: "This sign-in expired. Start again.",
  rate_limited: "Too many attempts. Wait a minute, then try again.",
  unavailable: "Sign-in is temporarily unavailable. Please try again in a moment.",
  code_spent: "We couldn't finish signing you in, and that code can't be used again. Get a new code and try again.",
  incomplete:
    "You're signed in, but we couldn't finish setting up your account. Reload the page to continue.",
};

export function signInError(message: string, reason: SignInErrorReason): SignInResult {
  return { status: "error", message, reason };
}

export function toSignInResult(outcome: SignInOutcome): SignInResult {
  switch (outcome.status) {
    case "signed_in":
      return {
        status: "signed_in",
        isNew: outcome.isNew,
        ...(outcome.quoteClaim
          ? { quoteClaim: outcome.quoteClaim === "redeemed" ? "redeemed" : "failed" }
          : {}),
      };
    case "needs_code":
    case "needs_hosted":
      return { status: outcome.status };
    case "failed":
      return signInError(FAILURE_MESSAGES[outcome.reason], outcome.reason);
    case "refused":
      return signInError(outcome.refusal.message, outcome.refusal.reason);
  }
}
